import React, {useCallback, useEffect, useRef, useState} from "react";
import {Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from "@mui/material";
import {environment} from "../../config/environment";
import {useAuth} from "../../shared/auth/AuthContext";
import {getMsUntilExpiration, getToken} from "../../shared/utils/session";
import {renovarSessao} from "./LoginService";
import {MS_AVISO, MS_INTERVALO_VERIFICACAO, decidir, formatarContagem} from "./renovacao";

/** Eventos que contam como "o usuário está aqui". */
const EVENTOS_DE_ATIVIDADE = ['pointerdown', 'keydown', 'wheel'];

/**
 * Mantém a sessão viva enquanto o backoffice está sendo usado, e pergunta antes de deixá-la
 * morrer quando não está. É o mesmo comportamento do busipet-front-admin.
 *
 * É um componente porque precisa de um diálogo. Toda a decisão mora em `./renovacao`, que é código
 * puro e testável. Aqui ficam só o relógio, os eventos do DOM e a chamada de rede.
 *
 * Monta uma vez, no topo do App. Dois destes rodando fariam duas renovações concorrentes.
 */
export default function VigiaDeSessao() {
    const {userLogged, authenticated, logout, renovarToken} = useAuth();
    const ativa = !environment.disableAuth && Boolean(authenticated) && Boolean(getToken(userLogged));

    const [avisoAberto, setAvisoAberto] = useState(false);
    const [renovando, setRenovando] = useState(false);
    const [msRestantes, setMsRestantes] = useState(() => getMsUntilExpiration(userLogged));

    // Refs e não estado: mudam a cada movimento do usuário e nenhuma delas deve provocar render.
    // Atividade em estado re-renderizaria a tela inteira a cada tecla digitada.
    const sessaoRef = useRef(userLogged);
    sessaoRef.current = userLogged;
    const ultimaAtividade = useRef(Date.now());
    const ultimaFalha = useRef(null);
    // Espelha `renovando` para o intervalo, que veria o valor do render em que foi criado.
    const renovandoRef = useRef(false);

    const marcarAtividade = useCallback(() => {
        ultimaAtividade.current = Date.now();
    }, []);

    useEffect(() => {
        if (!ativa) return undefined;

        // `capture` para enxergar o evento mesmo quando algum componente o interrompe antes do
        // document; `passive` porque nada aqui cancela nada.
        const opcoes = {capture: true, passive: true};
        EVENTOS_DE_ATIVIDADE.forEach((evento) => document.addEventListener(evento, marcarAtividade, opcoes));

        // Voltar para a aba conta como atividade: é quando renovar em silêncio é o que o usuário
        // quer, e um diálogo seria a primeira coisa que ele veria ao retomar o trabalho.
        const aoMudarVisibilidade = () => {
            if (document.visibilityState === 'visible') marcarAtividade();
        };
        document.addEventListener('visibilitychange', aoMudarVisibilidade);

        return () => {
            EVENTOS_DE_ATIVIDADE.forEach((evento) => document.removeEventListener(evento, marcarAtividade, opcoes));
            document.removeEventListener('visibilitychange', aoMudarVisibilidade);
        };
    }, [ativa, marcarAtividade]);

    const renovar = useCallback(async () => {
        if (renovandoRef.current) return;

        const token = getToken(sessaoRef.current);
        if (!token) return;

        renovandoRef.current = true;
        setRenovando(true);
        try {
            const response = await renovarSessao(token);
            const nova = response?.data?.valor || response?.data;
            if (!renovarToken(nova?.accessToken, nova?.expiresIn)) {
                // Resposta sem token ou sem expiração: a sessão atual foi preservada e continua
                // valendo. Contar como falha faz a próxima volta tentar de novo, e o aviso
                // aparecer se não der.
                ultimaFalha.current = Date.now();
                return;
            }
            ultimaFalha.current = null;
            // Clicar no aviso É atividade: sem isto, a sessão renovada voltaria a contar como
            // ociosa e o aviso reapareceria minutos depois.
            marcarAtividade();
            setAvisoAberto(false);
        } catch (falha) {
            ultimaFalha.current = Date.now();
            // 401 é sessão que o identity não aceita mais renovar: revogada, conta bloqueada ou
            // desativada, sem licença do sistema. Insistir prenderia o usuário num aviso que
            // nunca vai funcionar.
            if (falha?.response?.status === 401) {
                setAvisoAberto(false);
                logout();
            }
            // Qualquer outra falha (rede, 503, timeout) NÃO derruba a sessão: o token atual ainda
            // vale. A próxima volta tenta de novo, respeitando a espera.
        } finally {
            renovandoRef.current = false;
            setRenovando(false);
        }
    }, [logout, marcarAtividade, renovarToken]);

    useEffect(() => {
        if (!ativa) {
            setAvisoAberto(false);
            return undefined;
        }

        const verificar = () => {
            const restantes = getMsUntilExpiration(sessaoRef.current);
            setMsRestantes(restantes);

            // Outra aba renovou a sessão enquanto esta mostrava o aviso: ele perdeu o sentido.
            if (avisoAberto && restantes > MS_AVISO) {
                setAvisoAberto(false);
                return;
            }

            const agora = Date.now();
            const decisao = decidir({
                msRestantes: restantes,
                msDesdeAtividade: agora - ultimaAtividade.current,
                renovando: renovandoRef.current,
                avisoAberto,
                msDesdeFalha: ultimaFalha.current === null ? null : agora - ultimaFalha.current,
            });

            if (decisao === 'renovar') renovar();
            if (decisao === 'avisar') setAvisoAberto(true);
        };

        verificar();
        const temporizador = setInterval(verificar, MS_INTERVALO_VERIFICACAO);
        return () => clearInterval(temporizador);
    }, [ativa, avisoAberto, renovar]);

    // O contador do aviso anda de segundo em segundo; o intervalo de 5s da decisão deixaria o
    // número pulando. Só roda com o diálogo aberto.
    useEffect(() => {
        if (!avisoAberto) return undefined;
        const temporizador = setInterval(() => setMsRestantes(getMsUntilExpiration(sessaoRef.current)), 1_000);
        return () => clearInterval(temporizador);
    }, [avisoAberto]);

    if (!ativa) return null;

    return (
        <Dialog
            open={avisoAberto}
            // Fechar no Esc ou clicando fora não resolveria nada e devolveria o usuário a uma tela
            // que vai expirar em segundos. As duas saídas são botões.
            onClose={() => {}}
            disableEscapeKeyDown
            aria-labelledby="titulo-aviso-de-sessao"
        >
            <DialogTitle id="titulo-aviso-de-sessao">Sua sessão está prestes a expirar</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Por inatividade, sua sessão será encerrada em{' '}
                    <strong data-testid="contagem-sessao">{formatarContagem(msRestantes)}</strong>.
                    Continue conectado para não perder o que está fazendo.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => {
                        setAvisoAberto(false);
                        logout();
                    }}
                >
                    Sair agora
                </Button>
                <Button variant="contained" onClick={() => renovar()} disabled={renovando}>
                    {renovando ? 'Renovando...' : 'Continuar conectado'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
